import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { usePractitionerSurveys } from "../../hooks/useStudyDetails"
import {
  usePatchPatientSurveySubmission,
  usePostPatientSurveySubmission,
  useSurveys
} from "../../hooks/useSurveys"
import { IAnswer, ISurvey, PatientsFileTabsEnum } from "../../types/types"
import {
  checkQuestionsAnswered,
  orderQuestionsForSumoSurgeryDataSurvey
} from "../../utils/surveys"
import { PatientFilesTab } from "../layout/PatientFilesTab"
import Survey from "../Survey"

type Props = {
  closeModal: () => void
  patientId: string
}

export const PatientFileModal = ({ closeModal, patientId }: Props) => {
  const queryClient = useQueryClient()

  const { data: practitionerSurveys, isLoading: isLoadingPractitionerSurveys } =
    usePractitionerSurveys(patientId || "")
  const { data: surveys, isLoading: isSurveysLoading } = useSurveys()
  const [tab, setTab] = useState<PatientsFileTabsEnum>(
    PatientsFileTabsEnum.SURVEYS
  )
  const [answers, setAnswers] = useState<Array<IAnswer>>([])
  const [visibleSuccessMessage, setVisibleSuccessMessage] =
    useState<boolean>(false)

  const survey = orderQuestionsForSumoSurgeryDataSurvey(
    surveys?.find((_survey) => _survey.survey_reference === "sumo_surgery_data")
  )
  const practitionerSurvey = practitionerSurveys
    ? practitionerSurveys[0]
    : undefined
  const submissionAnswers = practitionerSurvey?.submission
  const practitionerAlreadySubmittedAnswers = !!submissionAnswers

  useEffect(() => {
    if (submissionAnswers?.answers.length) {
      const _answers = submissionAnswers?.answers.map((_ans) => {
        const _question = survey?.questions?.find(
          (_q) => _q?.id === _ans.question_id
        )
        if (_question?.kind === "MULTIPLE") {
          return typeof _ans.value === "string"
            ? {
                ..._ans,
                value: _ans.value.split(",").map((_val) => parseInt(_val))
              }
            : _ans
        }
        return _ans
      })
      setAnswers(_answers)
    }
  }, [submissionAnswers])

  const { mutateAsync: postSurvey, isPending: isPostSurveyPending } =
    usePostPatientSurveySubmission(
      practitionerSurveys && practitionerSurveys[0]?.id
    )
  const { mutateAsync: patchSurvey, isPending: isPatchSurveyPending } =
    usePatchPatientSurveySubmission(
      practitionerSurveys && practitionerSurveys[0]?.id
    )

  const onSubmitPostPatientSurvey = (survey: ISurvey | undefined) => {
    const sendSurvey = practitionerAlreadySubmittedAnswers
      ? patchSurvey
      : postSurvey
    if (survey && checkQuestionsAnswered(survey, answers)) {
      sendSurvey({
        submission: {
          answers_attributes:
            answers.map((_ans) => {
              return {
                ..._ans,
                value: Array.isArray(_ans.value)
                  ? _ans.value.toString()
                  : _ans.value
              }
            }) ?? []
        }
      }).then(() => {
        queryClient.invalidateQueries({
          queryKey: ["studydetails", patientId]
        })
        setVisibleSuccessMessage(true)
        setTimeout(() => setVisibleSuccessMessage(false), 2000)
      })
    }
  }

  const renderMedicalPatientData = () => <div></div>

  const renderSurvey = () => {
    return !isLoadingPractitionerSurveys && practitionerSurvey !== undefined ? (
      <div className="flex flex-col w-full h-full">
        {visibleSuccessMessage ? (
          <div className="flex flex-col w-full h-[3%]">
            <div className="text-sumo-550 font-bold text-sm">
              Les données ont été envoyées avec succès !
            </div>
          </div>
        ) : null}
        <div className="flex flex-col w-full h-[80%] overflow-auto">
          <Survey
            patientSurvey={{
              ...practitionerSurvey,
              patient_id: parseInt(patientId)
            }}
            survey={survey}
            setAnswers={setAnswers}
            answers={answers}
          />
        </div>
        <div className="flex flex-col w-full h-[15%] items-end justify-end">
          {renderSendButton()}
        </div>
      </div>
    ) : null
  }

  const renderSendButton = () => (
    <div className="flex h-full flex-col w-full">
      <div className="flex flex-col items-end justify-end flex-1 w-full">
        {!isPostSurveyPending && !isPatchSurveyPending ? (
          <button
            className="flex gap-2 bg-sumo-550 px-6 py-2 rounded-xl justify-center items-center text-sumo-50 font-semibold"
            onClick={() => onSubmitPostPatientSurvey(survey)}
          >
            <div className="flex-row flex items-end justify-end mx-2 my-1">
              {practitionerAlreadySubmittedAnswers
                ? "Mettre à jour"
                : "Envoyer"}
            </div>
          </button>
        ) : (
          <button className="bg-sumo-100 text-sumo-500 font-semibold px-6 py-2 rounded-xl">
            <div className="flex-row flex items-end justify-end mx-2 my-1">
              <p>Envoi</p>
              <div className="ml-2">
                <img
                  src="/images/fade-stagger-circles.svg"
                  style={{ height: "20px" }}
                  alt="close"
                  loading="lazy"
                />
              </div>
            </div>
          </button>
        )}
      </div>
    </div>
  )

  const renderTabsContent = () => {
    switch (tab) {
      case PatientsFileTabsEnum.DATA:
        return renderMedicalPatientData()
      case PatientsFileTabsEnum.SURVEYS:
        return renderSurvey()
    }
  }

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="h-3/4 w-1/2 my-6 mx-auto">
          <div className="rounded-xl shadow-lg bg-white outline-none focus:outline-none h-full">
            <div className="flex h-[15%] min-h-20 flex-col pl-4">
              <div className="flex items-center justify-between flex-1 w-full py-4 md:pt-6 px-4">
                <div className="flex flex-1">
                  <h3 className="text-cortexx-550 font-semibold">
                    Dossier Patient
                  </h3>
                </div>
                <div className="flex flex-1">
                  <button
                    className="p-1 ml-auto border-0 text-black float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                    onClick={closeModal}
                  >
                    <img
                      src="/images/close-circle.svg"
                      style={{ height: "25px" }}
                      alt="close"
                      loading="lazy"
                    />
                  </button>
                </div>
              </div>
              <div className="flex h-full">
                <PatientFilesTab setTab={setTab} tab={tab} />
              </div>
            </div>
            <div className="flex h-[85%] w-full flex-col px-8 py-4">
              {!isSurveysLoading ? renderTabsContent() : null}
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-45 fixed inset-0 z-40 bg-black"></div>
    </>
  )
}
